/* global trapFocus, removeTrapFocus */

// MARK:- Cart + Account tabbed drawer (#271). One dialog, the stock <cart-drawer>. Tab state is
// data-tab on that persistent element. The Account panel is one live node, captured here once and
// re-attached after every renderContents swap, so its contents, scroll, the upgraded
// <shop-login-button> and any focus inside it survive cart re-renders.
(() => {
  const drawer = document.getElementById('cart-drawer');
  const livePanel = document.getElementById('custom-account-panel');

  // MARK:- Buy again (#273). build is pure (the hub and QA share it): available lines only, plus the
  // /cart permalink the link's href already carries (no-JS fallback only). The click is document-level
  // so hub buttons work too.
  const build = (items) => {
    const lines = (Array.isArray(items) ? items : [])
      .filter((item) => item && item.available && item.id)
      .map((item) => ({ id: item.id, quantity: item.quantity }));
    const permalink = lines.length
      ? `/cart/${lines.map((line) => `${line.id}:${line.quantity}`).join(',')}`
      : null;
    return { items: lines, permalink };
  };
  window.customBuyAgain = { build };

  if (!drawer || !livePanel) return;

  const TTL = 5 * 60 * 1000;
  const FETCH_TIMEOUT = 8000;
  const SCRIPT_TIMEOUT = 5000;
  const SECTION_QUERY = '?section_id=custom-account-panel';
  const ZONE_LINK = '.custom-account-panel__zone-link';
  const RELOAD_FLAG = 'custom-account-drawer-login-reload';
  const ZONE_FALLBACK_DELAY = 4000;
  const FINDER_FOCUSABLE = 'input:not([type=hidden]):not([disabled]), button:not([disabled]), a[href], select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex^="-"])';
  const placeholder = Array.from(livePanel.childNodes, (node) => node.cloneNode(true));

  let gen = 0;
  let controller = null;
  let fetchedAt = 0;
  let fetchReason = null;
  const loadedSrcs = new Set();
  const inlineRan = new Set();
  let pendingTab = null;
  let pendingReason = null;
  let pendingFocus = null;
  let lastOpener = null;
  let finderWatch = null;
  let lastPanelFocus = null;
  let afterBoundary = false;
  let zoneTimer = null;

  const currentTab = () => drawer.dataset.tab || 'cart';
  const isOpen = () => drawer.hasAttribute('open');
  const ownsFocus = () => isOpen()
    && (drawer.contains(document.activeElement) || document.activeElement === document.body);
  const tabs = () => Array.from(drawer.querySelectorAll('[role="tab"]'));
  const selectedTab = () => tabs().find((tab) => tab.dataset.tab === currentTab());

  // MARK:- Tab state (Liquid always renders Cart selected; re-applied from data-tab after every render)
  const applyTabState = () => {
    const name = currentTab();
    tabs().forEach((tab) => {
      const selected = tab.dataset.tab === name;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    });
  };

  // MARK:- Re-trap focus against the whole drawer (trapFocus snapshots focusables once per call)
  const retrap = (preferred) => {
    if (!ownsFocus() || typeof trapFocus !== 'function') return;
    const usable = (el) => el && el.isConnected && drawer.contains(el) && el.getClientRects().length > 0;
    let target = selectedTab();
    if (usable(preferred)) target = preferred;
    else if (usable(document.activeElement)) target = document.activeElement;
    if (target) trapFocus(drawer, target);
  };

  // MARK:- Count in the tab strip mirrors the header bubble, which every cart path patches
  const syncCount = () => {
    const bubble = document.querySelector('#cart-icon-bubble [aria-hidden="true"]');
    const count = bubble ? bubble.textContent.trim() : '0';
    drawer.querySelectorAll('.custom-drawer-tabs__count').forEach((el) => {
      el.textContent = count;
    });
  };

  // MARK:- Scripts in the fragment never run through replaceChildren; run each once per page
  const runScripts = (root) => Promise.all(Array.from(root.querySelectorAll('script')).map((old) => {
    const src = old.getAttribute('src');
    if (src) {
      if (loadedSrcs.has(src)) return Promise.resolve();
      return new Promise((resolve) => {
        const script = document.createElement('script');
        Array.from(old.attributes).forEach(({ name, value }) => script.setAttribute(name, value));
        script.async = true;
        let cap = null;
        const done = (loaded) => {
          clearTimeout(cap);
          if (loaded) loadedSrcs.add(src);
          resolve();
        };
        cap = setTimeout(() => done(false), SCRIPT_TIMEOUT);
        script.addEventListener('load', () => done(true), { once: true });
        script.addEventListener('error', () => done(false), { once: true });
        document.head.append(script);
      });
    }

    const text = old.textContent;
    if (!text.trim() || inlineRan.has(text)) return Promise.resolve();
    inlineRan.add(text);
    const script = document.createElement('script');
    if (old.type) script.type = old.type;
    script.textContent = text;
    document.head.append(script);
    return Promise.resolve();
  }));

  // MARK:- Zone display fallback: cart attribute first (Liquid), then this browser's localStorage zone,
  // which also beats the saved-customer and address-ZIP zones Liquid falls back to (data-zone-source)
  const applyZoneFallback = (root) => {
    const row = root.querySelector('.custom-account-panel__zone');
    if (!row) return;
    if ((row.dataset.zone || '').trim() && (row.dataset.zoneSource || 'cart') === 'cart') return;
    let zone = null;
    let zip = null;
    try {
      zone = localStorage.getItem('growing_zone');
      zip = localStorage.getItem('growing_zipcode');
    } catch (e) {
      return;
    }
    if (!zone) return;
    const value = row.querySelector('.custom-account-panel__zone-value');
    if (!value) return;
    value.textContent = zip ? `${zone} (${zip})` : zone;
    value.classList.remove('hide');
    row.querySelector('.custom-account-panel__zone-empty')?.classList.add('hide');
  };

  // MARK:- Shop sign-in finished: refresh the panel; reload once if the session has not caught up
  const onLoginCompleted = async () => {
    const body = await fetchPanel('login');
    if (!body || body.dataset.signedIn !== 'false') return;
    try {
      if (sessionStorage.getItem(RELOAD_FLAG)) return;
      sessionStorage.setItem(RELOAD_FLAG, '1');
    } catch (e) {
      return;
    }
    window.location.reload();
  };

  const bindPanel = (root) => {
    root.querySelector('shop-login-button')?.addEventListener('completed', onLoginCompleted);
    const link = root.querySelector('.custom-account-panel__login-link');
    if (link) {
      const url = new URL(link.href, window.location.origin);
      url.searchParams.set('return_to', window.location.pathname + window.location.search);
      link.href = url.toString();
    }
  };

  // MARK:- Back to the Liquid placeholder (loading text, hidden fallback link); drops private content
  const clearPanel = () => {
    livePanel.replaceChildren(...placeholder.map((node) => node.cloneNode(true)));
    retrap();
  };

  // Loaders run from the parsed fragment first, so a response superseded meanwhile never reaches the DOM.
  const render = async (body, myGen) => {
    await runScripts(body);
    if (myGen !== gen) return false;
    const active = document.activeElement;
    const zoneFocused = livePanel.contains(active) && active.matches(ZONE_LINK);
    livePanel.replaceChildren(...body.childNodes);
    bindPanel(livePanel);
    applyZoneFallback(livePanel);
    livePanel.dataset.state = 'ready';
    afterBoundary = false;
    retrap(zoneFocused ? livePanel.querySelector(ZONE_LINK) : null);
    return true;
  };

  // MARK:- Failure: after a session boundary, never keep the previous content. Navigate only while the
  // placeholders are still showing (nothing rendered) and the user open is still active; otherwise a
  // failed background or TTL refetch keeps what is shown, or unhides the placeholder's fallback link.
  const fail = () => {
    livePanel.dataset.state = 'error';
    if (afterBoundary) clearPanel();
    const errorEl = livePanel.querySelector('.custom-account-panel__error');
    if (!errorEl) return;
    if (fetchReason === 'user' && isOpen() && currentTab() === 'account') {
      window.location.assign(livePanel.dataset.accountUrl);
      return;
    }
    errorEl.hidden = false;
    const loadingEl = livePanel.querySelector('.custom-account-panel__loading');
    if (loadingEl) loadingEl.hidden = true;
  };

  // MARK:- Lazy section fetch; generation counter + abort, newest request wins
  const fetchPanel = async (reason) => {
    gen += 1;
    const myGen = gen;
    fetchReason = reason;
    if (controller) controller.abort();
    const ctrl = new AbortController();
    controller = ctrl;
    const timer = setTimeout(() => ctrl.abort(), FETCH_TIMEOUT);
    if (livePanel.dataset.state !== 'ready') livePanel.dataset.state = 'loading';
    const loadingEl = livePanel.querySelector('.custom-account-panel__loading');
    const errorEl = livePanel.querySelector('.custom-account-panel__error');
    if (loadingEl) loadingEl.hidden = false;
    if (errorEl) errorEl.hidden = true;

    try {
      const opts = { credentials: 'same-origin', signal: ctrl.signal };
      let res = await fetch(`${window.location.pathname}${SECTION_QUERY}`, opts);
      if (!res.ok) res = await fetch(`${theme.routes.cart}${SECTION_QUERY}`, opts);
      if (!res.ok) throw new Error(`account panel ${res.status}`);
      const tmpl = document.createElement('template');
      tmpl.innerHTML = await res.text();
      const body = tmpl.content.querySelector('.custom-account-panel__body');
      if (!body) throw new Error('account panel body missing');
      if (myGen !== gen || !(await render(body, myGen))) return null;
      fetchedAt = Date.now();
      return body;
    } catch (err) {
      // Superseded requests (aborted by a newer one) are dropped silently; a timeout abort is a failure.
      if (myGen === gen) fail();
      return null;
    } finally {
      clearTimeout(timer);
      if (controller === ctrl) controller = null;
    }
  };

  const ensurePanel = (reason) => {
    // A request in flight is never restarted from here; a user open only promotes its reason.
    if (controller) {
      if (reason === 'user') fetchReason = 'user';
      return;
    }
    const { state } = livePanel.dataset;
    if (state === 'ready' && Date.now() - fetchedAt <= TTL) return;
    fetchPanel(reason);
  };

  const selectTab = (name, { focus = false } = {}) => {
    drawer.dataset.tab = name;
    applyTabState();
    if (name === 'account') ensurePanel('user');
    const tabEl = selectedTab();
    if (focus && tabEl) tabEl.focus();
    retrap(tabEl);
  };

  // MARK:- Invalidate: always bump gen and abort pending work, so no older response can flip stale to
  // ready. The replacement fetch runs now only when asked and Account is showing; else on the next open.
  const invalidate = (reason, { fetchNow = true, clear = false } = {}) => {
    gen += 1;
    if (controller) controller.abort();
    controller = null;
    if (clear) clearPanel();
    livePanel.dataset.state = clear ? 'empty' : 'stale';
    if (fetchNow && isOpen() && currentTab() === 'account') fetchPanel(reason);
  };

  // MARK:- Re-attach the live panel after renderContents replaces #cart-drawer's children
  // (childList only, no subtree: recorded deviation, cart-drawer.js has no post-render event)
  new MutationObserver(() => {
    const fresh = drawer.querySelector('#custom-account-panel');
    if (!fresh || fresh === livePanel) return;
    fresh.replaceWith(livePanel);
    drawer.dataset.renders = String((Number(drawer.dataset.renders) || 0) + 1);
    applyTabState();
    syncCount();
    retrap(currentTab() === 'account' ? lastPanelFocus : null);
  }).observe(drawer, { childList: true });

  // MARK:- Remember focus inside the panel so a re-attach can restore it (detaching drops focus)
  drawer.addEventListener('focusin', (evt) => {
    lastPanelFocus = livePanel.contains(evt.target) ? evt.target : null;
  });

  // MARK:- Open routing: the tab comes from whoever opened the drawer (cart by default)
  document.addEventListener('on:cart-drawer:before-open', (evt) => {
    if (evt.target !== drawer) return;
    const tab = pendingTab || 'cart';
    const reason = pendingReason || 'open';
    pendingTab = null;
    pendingReason = null;
    drawer.dataset.tab = tab;
    applyTabState();
    if (tab === 'account') ensurePanel(reason);
    // The stock open sets this.opener and traps focus right after dispatching; run after it.
    setTimeout(() => {
      if (!drawer.opener && lastOpener) drawer.opener = lastOpener;
      lastOpener = null;
      if (pendingFocus === 'zone-link') {
        pendingFocus = null;
        retrap(livePanel.querySelector(ZONE_LINK));
      }
    }, 0);
  });

  document.addEventListener('on:cart-drawer:after-open', (evt) => {
    if (evt.target !== drawer) return;
    pendingTab = null;
    pendingFocus = null;
  });

  // MARK:- Header account icon opens the drawer on Account; href stays the no-JS fallback
  const accountIcon = document.getElementById('account-icon');
  if (accountIcon) {
    accountIcon.setAttribute('role', 'button');
    accountIcon.setAttribute('aria-haspopup', 'dialog');
    accountIcon.setAttribute('aria-controls', 'cart-drawer');
    const openAccount = (evt) => {
      if (typeof drawer.open !== 'function') return;
      evt.preventDefault();
      pendingTab = 'account';
      pendingReason = 'user';
      try {
        drawer.open(accountIcon, document.getElementById('custom-drawer-tab-account'));
      } catch (err) {
        window.location.assign(accountIcon.href);
      }
    };
    accountIcon.addEventListener('click', openAccount);
    accountIcon.addEventListener('keydown', (evt) => {
      if (evt.key === ' ') openAccount(evt);
    });
  }

  // MARK:- Header cart icon always opens on Cart
  const cartIcon = document.getElementById('cart-icon');
  if (cartIcon) {
    const routeToCart = () => {
      pendingTab = 'cart';
      pendingReason = null;
    };
    cartIcon.addEventListener('click', routeToCart, true);
    cartIcon.addEventListener('keydown', routeToCart, true);
  }

  // MARK:- ARIA tabs: click and automatic activation on arrow keys, Home, End
  drawer.addEventListener('click', (evt) => {
    const tab = evt.target.closest('[role="tab"]');
    if (tab && drawer.contains(tab)) selectTab(tab.dataset.tab, { focus: true });
  });

  drawer.addEventListener('keydown', (evt) => {
    const tab = evt.target.closest && evt.target.closest('[role="tab"]');
    if (!tab) return;
    const list = tabs();
    const index = list.indexOf(tab);
    let next = null;
    if (evt.key === 'ArrowRight') next = list[(index + 1) % list.length];
    else if (evt.key === 'ArrowLeft') next = list[(index - 1 + list.length) % list.length];
    else if (evt.key === 'Home') [next] = list;
    else if (evt.key === 'End') next = list[list.length - 1];
    if (!next) return;
    evt.preventDefault();
    selectTab(next.dataset.tab, { focus: true });
  });

  // MARK:- First-order signup: close the drawer, then open the Klaviyo popup above the page
  livePanel.addEventListener('click', (evt) => {
    const btn = evt.target.closest('[data-klaviyo-form]');
    if (!btn) return;
    const formId = btn.dataset.klaviyoForm;
    drawer.close(() => {
      // MARK:- Consent hardening (#3263): klaviyo.js loads only after marketing consent, so re-read consent first
      const consent = window.nhConsent;
      if (consent && typeof consent.refresh === 'function') consent.refresh('welcome-form');
      // GPC denies marketing whatever the banner says, so neither the form nor the preferences dialog can help
      if (consent && consent.gpc()) return;
      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push(['openForm', formId]);
      // Not allowed yet: open the banner preferences; the queued openForm runs once klaviyo.js loads
      if (consent && !consent.allowed('marketing') && window.privacyBanner && typeof window.privacyBanner.showPreferences === 'function') {
        window.privacyBanner.showPreferences();
      }
    });
  });

  // MARK:- Zone "Change" inside the panel: finder focus hand-off (desktop reopen, mobile overlay)
  document.addEventListener('click', (evt) => {
    const link = evt.target.closest && evt.target.closest(ZONE_LINK);
    if (!link || !livePanel.contains(link)) return;
    const finder = document.getElementById('FindGrowZoneDrawer');
    if (!finder || finder.classList.contains('open')) return;
    finder.dataset.lastTriggerSelector = '';

    if (window.innerWidth > 768) {
      // custom-zone-verdict.js closes the drawer and reopens it when the finder closes (same open guard).
      lastOpener = drawer.opener;
      pendingTab = 'account';
      pendingFocus = 'zone-link';
      return;
    }

    if (finderWatch) finderWatch.disconnect();
    let finderOpened = false;
    let escapeDown = false;
    const noteKey = (keyEvt) => {
      escapeDown = keyEvt.key === 'Escape';
    };
    finderWatch = new MutationObserver(() => {
      if (finder.classList.contains('open')) {
        if (finderOpened) return;
        // Finder is open over the drawer: suspend the drawer's modal and trap focus in the finder.
        finderOpened = true;
        document.addEventListener('keydown', noteKey, true);
        drawer.setAttribute('aria-modal', 'false');
        const focusables = Array.from(finder.querySelectorAll(FINDER_FOCUSABLE))
          .filter((el) => el.getClientRects().length > 0);
        const input = focusables.find((el) => el.id === 'GrowZoneZipCodeInput');
        if (typeof trapFocus === 'function') trapFocus(finder, input || focusables[0] || finder);
        return;
      }
      if (!finderOpened) return;
      finderWatch.disconnect();
      finderWatch = null;
      document.removeEventListener('keydown', noteKey, true);
      drawer.setAttribute('aria-modal', 'true');
      // The finder closes on Escape keydown; swallow that keyup so the drawer's own Escape does not close it.
      if (escapeDown) {
        document.addEventListener('keyup', (keyEvt) => {
          if (keyEvt.key === 'Escape') keyEvt.stopPropagation();
        }, { capture: true, once: true });
      }
      if (isOpen() && typeof trapFocus === 'function') {
        trapFocus(drawer, livePanel.querySelector(ZONE_LINK) || drawer);
      }
    });
    finderWatch.observe(finder, { attributes: true, attributeFilter: ['class'] });
  }, true);

  // MARK:- Invalidation and revalidation
  // zone:updated precedes the cart attribute write: mark stale now, refetch once the write lands
  // (fallback timer if it never does, as custom-zone-verdict.js does).
  document.addEventListener('zone:updated', () => {
    invalidate('zone', { fetchNow: false });
    clearTimeout(zoneTimer);
    zoneTimer = setTimeout(() => invalidate('zone'), ZONE_FALLBACK_DELAY);
  });
  document.addEventListener('cart:attribute-updated', () => {
    clearTimeout(zoneTimer);
    invalidate('zone');
  });

  // Session boundaries: leaving the page (hidden, pagehide) drops private content to the placeholder;
  // returning or a bfcache restore refetches at once when Account is showing (loading meanwhile),
  // else on the next open.
  const leave = () => {
    afterBoundary = true;
    invalidate('leave', { fetchNow: false, clear: true });
  };
  window.addEventListener('pagehide', leave);

  window.addEventListener('pageshow', (evt) => {
    if (!evt.persisted) return;
    afterBoundary = true;
    invalidate('bfcache', { clear: true });
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      leave();
      return;
    }
    afterBoundary = true;
    invalidate('visible', { clear: true });
  });

  // MARK:- Cart events: keep the count current; re-trap the whole drawer when cart-items trapped hidden
  // controls (Account showing) or scoped its trap to .js-cart-empty (last item removed on Cart)
  document.addEventListener('on:cart:add', syncCount);
  ['on:line-item:change', 'on:cart-items:refresh'].forEach((name) => {
    document.addEventListener(name, () => {
      syncCount();
      if (currentTab() === 'account' || !drawer.querySelector('.cart-item')) retrap();
    });
  });

  // MARK:- Buy again click (#273): plain left clicks only; modified clicks keep the permalink href
  document.addEventListener('click', async (evt) => {
    const button = evt.target.closest && evt.target.closest('[data-buy-again]');
    if (!button || evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.button !== 0) return;
    let items = [];
    try {
      ({ items } = build(JSON.parse(button.dataset.buyAgain)));
    } catch (e) {
      return;
    }
    if (!items.length) return;
    evt.preventDefault();
    if (button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');

    // MARK:- Standard storefront event at operation start, settled on every path (docs/javascript-patterns.md)
    const standardEvents = window.StandardEvents;
    let deferred = null;
    if (standardEvents && standardEvents.CartLinesUpdateEvent) {
      deferred = standardEvents.CartLinesUpdateEvent.createPromise();
      button.dispatchEvent(new standardEvents.CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: item.id, quantity: item.quantity })),
        promise: deferred.promise
      }));
    }

    // The JS path never follows the /cart permalink (it replaces the cart); the href is the no-JS fallback.
    const postAdd = (lines) => fetch(theme.routes.cartAdd, {
      method: 'POST',
      headers: {
        Accept: 'application/javascript',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ items: lines })
    });
    // The drawer's markup is from page load, so refresh it whether it is open or about to open.
    const showCart = () => {
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
      if (isOpen()) {
        selectTab('cart');
      } else {
        pendingTab = 'cart';
        pendingReason = null;
        drawer.open(button);
      }
    };

    const readCart = () => fetch(`${theme.routes.cart}.js`)
      .then((res) => (res.ok ? res.json() : null))
      .catch(() => null);
    const qtyByVariant = (cart) => {
      const map = new Map();
      cart.items.forEach((line) => map.set(line.variant_id, (map.get(line.variant_id) || 0) + line.quantity));
      return map;
    };
    const errorOf = async (res) => {
      const data = await res.json().catch(() => ({}));
      // Fallback copy comes from the link (cart.general.error), as the panel's other strings come from Liquid.
      return typeof data.description === 'string' ? data.description : data.message || button.dataset.buyAgainError;
    };
    // cart-items clears #cart-errors when it re-renders, so write the note after that refresh (timer if none comes).
    const noteErrors = (message) => {
      let timer = null;
      // Whichever path wins removes the other, so a late refresh never shows a stale note.
      const write = () => {
        clearTimeout(timer);
        document.removeEventListener('on:cart-items:refresh', write);
        const el = document.getElementById('cart-errors');
        if (!el) return;
        el.textContent = message;
        el.hidden = false;
      };
      document.addEventListener('on:cart-items:refresh', write, { once: true });
      timer = setTimeout(write, 2000);
    };

    let settled = false;
    try {
      const before = await readCart();
      let response = null;
      try {
        response = await postAdd(items);
      } catch (err) {
        // Network failure: settle as a failed request and show the cart as it stands.
        settled = true;
        if (deferred) deferred.reject(err);
        if (standardEvents && standardEvents.CartErrorEvent) {
          button.dispatchEvent(new standardEvents.CartErrorEvent({ error: 'Cart request failed', code: 'SERVICE_UNAVAILABLE' }));
        }
        showCart();
        return;
      }

      const errors = [];
      let cartData = await readCart();
      if (!response.ok) {
        const batchError = await errorOf(response);
        if (!before || !cartData) {
          // Without both snapshots a retry could double-add; leave the cart as it is.
          errors.push(batchError);
        } else {
          // MARK:- A declined batch (e.g. 422 over stock) can still add the available quantity; retry only
          // each variant's shortfall (requested minus the increase already applied), skipping refusals
          const requested = new Map();
          items.forEach(({ id, quantity }) => requested.set(id, (requested.get(id) || 0) + quantity));
          const was = qtyByVariant(before);
          const now = qtyByVariant(cartData);
          let retried = false;
          for (const [id, quantity] of requested) {
            const shortfall = quantity - ((now.get(id) || 0) - (was.get(id) || 0));
            if (shortfall > 0) {
              retried = true;
              // eslint-disable-next-line no-await-in-loop
              const single = await postAdd([{ id, quantity: shortfall }]).catch(() => null);
              // eslint-disable-next-line no-await-in-loop
              if (!single || !single.ok) errors.push(single ? await errorOf(single) : batchError);
            }
          }
          if (retried) cartData = (await readCart()) || cartData;
        }
      }

      const anyAdded = response.ok || Boolean(before && cartData && cartData.item_count > before.item_count);
      const message = Array.from(new Set(errors)).join(' ');
      settled = true;
      if (deferred) {
        if (anyAdded) {
          deferred.resolve({ cart: cartData ? standardEvents.CartLinesUpdateEvent.createCartFromAjaxResponse(cartData) : null });
        } else {
          deferred.resolve({ cart: null, userErrors: [{ code: 'INVALID', field: [], message }] });
        }
      }
      if (anyAdded && cartData) {
        button.dispatchEvent(new CustomEvent('on:cart:add', {
          bubbles: true,
          detail: { cart: cartData, variantId: items[0].id }
        }));
      }
      showCart();
      if (message) noteErrors(message);
    } catch (err) {
      if (deferred && !settled) deferred.resolve({ cart: null });
    } finally {
      button.removeAttribute('aria-busy');
    }
  });
})();
